import * as React from "react";

import {
  AccessOverviewEditChartColors,
  AccessReportsAdminPageOptionsEnum,
  AccessSummaryEditChartColors,
  AccessSummaryEditChartColorsElementEnum,
  EditPrintColumns
} from "@app/access-reports/admin/";
import {
  AccessRadiusSearchPageElementEnum,
  AccessReportsColumn,
  AccessReportsPageOptionsElementEnum,
  AccessReportsWizardElement,
  AccessReportsWizardElementTypeEnum,
  AccessReportsWizardFormGroup,
  AccessReportsWizardGroupPropertyIdEnum,
  AccessReportsWizardOption,
  AccessReportsWizardPage,
  AccessReportsWizardProduct,
  AccessReportsWizardStepEnum,
  AccessReportsWizardTemplate,
  ByProductDefault,
  ColumnDefaults,
  PageSelectionElementIdEnum
} from "@app/access-reports/shared";
import { LoadingSpinner } from "@app/shared";
import cx from "classnames";
import { QESButton, QESSelect, QESTextArea, QESTextInput } from "../../shared/qes-standards";
import { QESCheckbox } from "../../shared/qes-standards/forms/Checkbox";
import { getFormGroupByGroupPropertyIdFromTemplate } from "../shared/utility";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { JSX } from "react";

interface AccessReportsAdminAddProductPageOptionsProps {
  disableUserActions: boolean;
  invalidPageOptionPageElements: Map<string, string>;
  onAddPageClick: (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum) => void;
  onDisclaimerTextChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onPageOptionPageChange: (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, byProductDefaultId: number, elementId: string, value: string | number | boolean) => void;
  onPageOptionPageToggle: (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, key: AccessReportsAdminPageOptionsEnum) => void;
  onPageOptionToggle: (elementId: string, key: AccessReportsAdminPageOptionsEnum) => void;
  onRemoveFormGroupClick: (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, byProductDefaultId: number) => void;
  onSaveColumnsClick: (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, columnDefaults: ColumnDefaults) => void;
  onSaveAccessOverviewChartColorsClick: (byProductDefault: ByProductDefault) => void;
  onSaveAccessSummaryChartColorsClick: (byProductDefault: ByProductDefault) => void;
  product: AccessReportsWizardProduct;
  template: AccessReportsWizardTemplate;
}

interface AccessReportsAdminAddProductPageOptionsState {
  sidePanelVisible: boolean;
}

interface OptionsTableData {
  [wafflesKeyColumnName]: string;
  blank: JSX.Element;
  default: JSX.Element;
  hide: JSX.Element;
  title: string;
}

interface PageTableData {
  [wafflesKeyColumnName]: string;
  allowMultiple: JSX.Element;
  blank: JSX.Element;
  default: JSX.Element;
  hide: JSX.Element;
  title: string;
}

class AccessReportsAdminAddProductPageOptions extends React.Component<AccessReportsAdminAddProductPageOptionsProps, AccessReportsAdminAddProductPageOptionsState> {
  constructor(props: AccessReportsAdminAddProductPageOptionsProps) {
    super(props);

    this.state = {
      sidePanelVisible: false,
    };
  }

  public render() {
    if (!!this.props.template) {
      return (
        <React.Fragment>
          {this.buildPageOptionsSection()}
          {this.buildDisclaimerPageSection()}
          {this.buildPages()}
        </React.Fragment>
      );
    } else {
      return (
        <div className="row">
          <div className="col-12">
            <LoadingSpinner />
          </div>
        </div>
      );
    }
  }

  public hideSidePanel = (): void => {
    if (this.state.sidePanelVisible) {
      this.setState({
        sidePanelVisible: false,
      });
    }
  }

  public onSaveAccessSummaryChartColorsClick = (byProductDefault: ByProductDefault): void => {
    this.props.onSaveAccessSummaryChartColorsClick(byProductDefault);
    this.hideSidePanel();
  }

  public onSaveAccessOverviewChartColorsClick = (byProductDefault: ByProductDefault): void => {
    this.props.onSaveAccessOverviewChartColorsClick(byProductDefault);
    this.hideSidePanel();
  }

  public onSaveColumnsClick = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, columnDefaults: ColumnDefaults): void => {
    this.props.onSaveColumnsClick(groupPropertyId, columnDefaults);
    this.hideSidePanel();
  }

  public buildDisclaimerPageSection = (): React.ReactNode => {
    const pageOptionsFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(this.props.template, AccessReportsWizardStepEnum.PageOptions);
    const legalDisclaimerElement: AccessReportsWizardElement = pageOptionsFormGroup?.elements.find(x => x.elementId === AccessReportsPageOptionsElementEnum.LegalDisclaimer);

    if (!!legalDisclaimerElement?.options) {
      const productOption: AccessReportsWizardOption = legalDisclaimerElement.options.find(x => x.id === this.props.product.id);

      if (!!productOption) {
        // the disclaimer page text area will remain disabled
        const enableDisclaimerTextarea: boolean = this.getCheckboxValue(legalDisclaimerElement, AccessReportsAdminPageOptionsEnum.Default);

        return (
          <React.Fragment>
            {this.buildOptionsTable([legalDisclaimerElement], "Disclaimer Page")}

            <div className="row">
              <div className="col-12">
                <hr className="mt-0" />
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <div className="access-reports-pages-label">
                  <div className="form-group">
                    <QESTextArea
                      label="Legal Disclaimer Text"
                      disabled={!enableDisclaimerTextarea || this.props.disableUserActions}
                      testId="access-reports-admin__add-product--disclaimer-textarea"
                      maxLength={32768}
                      onChange={(e) => this.props.onDisclaimerTextChange(e)}
                      value={productOption.name}
                    />
                  </div>
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      }
    }
  }

  private buildPageOptionsSection = (): React.ReactNode => {
    const pageOptionsFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(this.props.template, AccessReportsWizardStepEnum.PageOptions);
    const excludedElements: AccessReportsPageOptionsElementEnum[] = [
      AccessReportsPageOptionsElementEnum.LegalDisclaimer,
      this.props.product.skipAccessAnalysis && AccessReportsPageOptionsElementEnum.SplitEmployeeGroupByStandard,
      this.props.product.skipAccessAnalysis && AccessReportsPageOptionsElementEnum.SplitPagesBySpecialty,
    ];
    const filteredPageOptionsFormGroups: AccessReportsWizardElement[] = pageOptionsFormGroup?.elements.filter(x => !excludedElements.includes(x.elementId as AccessReportsPageOptionsElementEnum));

    if (filteredPageOptionsFormGroups.length) {
      return this.buildOptionsTable(filteredPageOptionsFormGroups);
    }
  }

  private buildOptionsTable = (pageOptions: AccessReportsWizardElement[], title?: string): React.ReactNode => {
    const columnConfig = {
      blank: {
        displayText: "",
        noSort: true,
        testId: "blank",
      },
      default: {
        displayText: "Default",
        noSort: true,
        testId: "default",
      },
      hide: {
        displayText: "Hide",
        noSort: true,
        testId: "hide",
      },
      title: {
        displayText: title ? title : "Option",
        noSort: true,
        testId: "title",
      },
    };

    const data = pageOptions.map((pageOption) => {
      // note: disclaimer and separator pages will always be hidden,
      // therefore, the "hidden" checkbox should always remain disabled
      // and the "default" checkbox should never be disabled
      const defaultCheckbox: boolean = this.getCheckboxValue(pageOption, AccessReportsAdminPageOptionsEnum.Default);
      const hideCheckbox: boolean = this.getCheckboxValue(pageOption, AccessReportsAdminPageOptionsEnum.Hide);
      /* eslint-disable sort-keys */
      return {
        [wafflesKeyColumnName]: pageOption.elementId,
        title: this.splitKey(pageOption.elementId),
        default: (
          <QESCheckbox
            checked={defaultCheckbox}
            className="m-1"
            disabled={this.props.disableUserActions || (
              hideCheckbox &&
              pageOption.elementId !== AccessReportsPageOptionsElementEnum.SeparatorPage &&
              pageOption.elementId !== AccessReportsPageOptionsElementEnum.LegalDisclaimer
            )}
            label=""
            testId={`access-reports-admin__add-product--default-${pageOption.elementId}`}
            onChange={() => this.props.onPageOptionToggle(pageOption.elementId, AccessReportsAdminPageOptionsEnum.Default)}
          />
        ),
        hide: (
          <QESCheckbox
            checked={hideCheckbox}
            className="m-1"
            disabled={this.props.disableUserActions || (
              defaultCheckbox ||
              pageOption.elementId === AccessReportsPageOptionsElementEnum.SeparatorPage ||
              pageOption.elementId === AccessReportsPageOptionsElementEnum.LegalDisclaimer
            )}
            label=""
            testId={`access-reports-admin__add-product--hide-${pageOption.elementId}`}
            onChange={() => this.props.onPageOptionToggle(pageOption.elementId, AccessReportsAdminPageOptionsEnum.Hide)}
          />
        ),
        blank: <>&nbsp;</>,
      }
      /* eslint-enable */
    })
    return (
      <div className="access-reports-wizard-admin-table">
        <Waffles<OptionsTableData>
          columns={columnConfig}
          data={data}
          disableProcessingOverlay={true}
          disableStickyHeader={true}
          fullWidth={true}
          testId={`page-options${title ? `-${title}` : "-option"}`}
        />
      </div>
    );
  }

  private buildPages = (): React.ReactNode => {
    const columnConfig = {
      allowMultiple: {
        displayText: "Allow Multiple",
        noSort: true,
        testId: "allow-multiple",
      },
      blank: {
        displayText: "",
        noSort: true,
        testId: "blank",
      },
      default: {
        displayText: "Default",
        noSort: true,
        testId: "default",
      },
      hide: {
        displayText: "Hide",
        noSort: true,
        testId: "hide",
      },
      title: {
        displayText: "Option",
        noSort: true,
        testId: "title",
      },
    };

    let groupPropertyIds: AccessReportsWizardGroupPropertyIdEnum[] = [];

    if (this.props.product.skipAccessAnalysis) {
      groupPropertyIds = [AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages];
    } else {
      groupPropertyIds = Object.values(AccessReportsWizardGroupPropertyIdEnum);
    }

    return (
      <React.Fragment>
        {groupPropertyIds.map((groupPropertyId, index) => {
          const pageName: string = this.getPageName(groupPropertyId);
          const defaultCheckbox: boolean = this.getPagesCheckboxValue(groupPropertyId, AccessReportsAdminPageOptionsEnum.Default);
          const hideCheckbox: boolean = this.getPagesCheckboxValue(groupPropertyId, AccessReportsAdminPageOptionsEnum.Hide);
          const allowMultipleCheckbox: boolean = this.getPagesCheckboxValue(groupPropertyId, AccessReportsAdminPageOptionsEnum.AllowMultiple);

          const pageOptionsPage: AccessReportsWizardPage = this.props.template.pages.find(x => x.title === AccessReportsWizardStepEnum.PageOptions);
          const pageFormGroup: AccessReportsWizardFormGroup = pageOptionsPage?.formGroups.find(x => x.groupPropertyId === groupPropertyId);
          const byProductDefaults: ByProductDefault[] = pageFormGroup.byProductDefaults.filter(x => x.productId === this.props.product.id);

          /* eslint-disable sort-keys */
          const data = [{
            [wafflesKeyColumnName]: pageName,
            title: pageName,
            default: (
              <QESCheckbox
                checked={defaultCheckbox}
                className="m-1"
                disabled={hideCheckbox || this.props.disableUserActions}
                label=""
                testId={`access-reports-admin__add-product--default-${groupPropertyId}`}
                onChange={() => this.props.onPageOptionPageToggle(groupPropertyId, AccessReportsAdminPageOptionsEnum.Default)}
              />
            ),
            hide: (
              <QESCheckbox
                checked={hideCheckbox}
                className="m-1"
                disabled={defaultCheckbox || this.props.disableUserActions}
                label=""
                testId={`access-reports-admin__add-product--hide-${groupPropertyId}`}
                onChange={() => this.props.onPageOptionPageToggle(groupPropertyId, AccessReportsAdminPageOptionsEnum.Hide)}
              />
            ),
            allowMultiple: (
              <QESCheckbox
                checked={allowMultipleCheckbox}
                className="m-1"
                disabled={hideCheckbox || this.props.disableUserActions}
                label=""
                testId={`access-reports-admin__add-product--allow-multiple-${groupPropertyId}`}
                onChange={() => this.props.onPageOptionPageToggle(groupPropertyId, AccessReportsAdminPageOptionsEnum.AllowMultiple)}
              />
            ),
            blank: this.getSidePanel(groupPropertyId)
          }];
          /* eslint-enable */

          if (byProductDefaults.length) {
            return (
              <React.Fragment key={index}>
                <div className="row">
                  <div className="col-12">
                    <div className="access-reports-wizard-admin-table">
                      <Waffles<PageTableData>
                        columns={columnConfig}
                        data={data}
                        disableProcessingOverlay={true}
                        disableStickyHeader={true}
                        fullWidth={true}
                        testId={`access-reports-page-options-${groupPropertyId}`}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <hr className="mt-0" />
                  </div>
                </div>
                <React.Fragment>
                  {byProductDefaults.map((byProductDefault, byProductDefaultIndex) => (
                    <React.Fragment key={byProductDefaultIndex}>
                      {this.buildPageOptionsPageDefaults(byProductDefault, pageName, pageFormGroup, byProductDefaultIndex, hideCheckbox)}
                    </React.Fragment>
                  ))}
                  <div className="row">
                    <div className="offset-md-2 col-md-10">
                      <div className="mb-3">
                        <QESButton
                          className="p-0"
                          qesTestId={`access-reports-admin__add-product--add-another-${groupPropertyId}`}
                          qesType="secondary"
                          onClick={() => this.props.onAddPageClick(groupPropertyId)}
                          disabled={hideCheckbox || this.props.disableUserActions}
                        >
                          <i className="fa fa-plus" /> Add Another {pageName} Page
                        </QESButton>
                      </div>
                    </div>
                  </div>
                </React.Fragment>

              </React.Fragment>
            );
          }
        })}
      </React.Fragment>
    );
  }

  private buildPageOptionsPageDefaults = (
    byProductDefault: ByProductDefault,
    pageName: string,
    formGroup: AccessReportsWizardFormGroup,
    formGroupIndex: number,
    disabled: boolean,
  ): React.ReactNode => {
    let elements: AccessReportsWizardElement[] = [];

    switch (formGroup.groupPropertyId) {
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages:
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardDetailsPages:
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage:
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages:
      case AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages:
        elements = formGroup.elements.filter(
          x => x.elementType === AccessReportsWizardElementTypeEnum.Dropdown && !x.hidden
        );
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessListingPages:
        elements = formGroup.elements.filter(
          x => x.elementType === AccessReportsWizardElementTypeEnum.Dropdown || x.elementType === AccessReportsWizardElementTypeEnum.Input
        );
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch:
        elements = formGroup.elements.filter(
          x => x.elementId !== AccessRadiusSearchPageElementEnum.AccessRadiusSearch && x.elementType !== AccessReportsWizardElementTypeEnum.Unknown
        );
        break;
      default:
        console.error(`Unable to retrieve elements due to invalid Group Property Id - ${formGroup.groupPropertyId}`);
        break;
    }

    return (
      elements.length > 0 ? (
        <div className="row align-items-center">
          <div className="col-md-2">
            {formGroupIndex === 0 &&
              <label
                className={cx({
                  "access-reports-pages-label": true,
                  "label-s": true,
                  "text-muted": disabled,
                })}
              >
                Set {pageName}
              </label>
            }
          </div>
          <div className="col-md-9">
            <div className="row">
              {elements.map((element, index) => (
                <div className="col-md-4" key={index}>
                  {this.getPageOptionsPageElement(byProductDefault, element, formGroup, formGroupIndex, disabled)}
                </div>
              ))}
            </div>
          </div>

          {formGroupIndex !== 0 && (
            <div className="col-1">
              {!this.props.disableUserActions ? (
                <QESButton
                  qesType="secondary-danger"
                  qesTestId={`access-reports-admin__add-product--remove-form-group-${formGroupIndex}`}
                  id={`access-reports-admin__add-product--remove-form-group-${formGroupIndex}`}
                  className="text-danger mr-3 cursor-pointer font-weight-bold p-0"
                  onClick={() => this.props.onRemoveFormGroupClick(formGroup.groupPropertyId, byProductDefault.id)}
                >
                  Remove
                </QESButton>
              ) : (
                <span
                  className="text-muted mr-3 cursor-pointer font-weight-bol"
                >
                  Remove
                </span>
              )}

            </div>
          )}
        </div>
      ) : (
        <div className="row">
          <div className="col-12">
            <p>
              <i className="text-danger fa fa-fw fa-exclamation-circle mr-1" />
              Error - There are no elements to display
            </p>
          </div>
        </div>
      )
    );
  }

  private getCheckboxValue = (pageOptionElement: AccessReportsWizardElement, key: AccessReportsAdminPageOptionsEnum): boolean => {
    const pageOptionsFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(this.props.template, AccessReportsWizardStepEnum.PageOptions);
    const pageOption: AccessReportsWizardElement = pageOptionsFormGroup.elements.find(x => x.elementId === pageOptionElement.elementId);
    let checkboxValue = false;

    const pageOptionProductOption: AccessReportsWizardOption = pageOption.options?.find(x => x.id === this.props.product.id);

    if (!!pageOptionProductOption) {
      switch (key) {
        case AccessReportsAdminPageOptionsEnum.Default:
          if (pageOptionProductOption.defaultProducts?.includes(this.props.product.id)) {
            checkboxValue = true;
          }

          break;
        case AccessReportsAdminPageOptionsEnum.Hide:
          if (pageOptionProductOption.hiddenProducts?.includes(this.props.product.id)) {
            checkboxValue = true;
          }

          break;
        default:
          console.error("Invalid Admin Page Option");
          break;
      }
    }

    return checkboxValue;
  }

  private getPageName = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum): string => {
    switch (groupPropertyId) {
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardDetailsPages:
        return "Access Detail";
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages:
        return "Access Summary";
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage:
        return "Employee Count";
      case AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages:
        return "Access Overview";
      case AccessReportsWizardGroupPropertyIdEnum.AccessListingPages:
        return "Access Listing";
      case AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch:
        return "Access Radius Search";
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages:
        return "Provider Count";
      default:
        return "Invalid Group Property Id";
    }
  }

  private getPageOptionPageDropdownOptions = (
    element: AccessReportsWizardElement,
    formGroup: AccessReportsWizardFormGroup,
  ): AccessReportsWizardOption[] => {
    let options: AccessReportsWizardOption[] = [];

    if (element.previousRequiredElementId && element.usePreviousElementFilterId && formGroup) {
      const previousRequiredElement = formGroup.elements.find((x) => x.elementId === element.previousRequiredElementId);

      if (previousRequiredElement?.previousRequiredValueId !== null && previousRequiredElement?.previousRequiredValueId !== undefined) {
        options = element.options.filter((x) => (
          (x.previousElementFilterId !== null && x.previousElementFilterId !== undefined) &&
          (x.previousElementFilterId.toString() === previousRequiredElement.previousRequiredValueId.toString()))
        );
      }
    } else {
      options = element.options;
    }

    return options;
  }

  private getPagesCheckboxValue = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, key: AccessReportsAdminPageOptionsEnum): boolean => {
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(this.props.template, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);

    if (!!pageFormGroup) {
      const firstByProductDefault: ByProductDefault = pageFormGroup.byProductDefaults.find(x => x.productId === this.props.product.id);

      switch (key) {
        case AccessReportsAdminPageOptionsEnum.Default:
          let defaulted = false;

          for (const property of Object.keys(firstByProductDefault)) {
            if (Object.values(PageSelectionElementIdEnum).includes(property as PageSelectionElementIdEnum)) {
              defaulted = firstByProductDefault[property] as boolean;
              break;
            }
          }

          return defaulted;
        case AccessReportsAdminPageOptionsEnum.Hide:
          if (pageFormGroup.hiddenProducts.includes(this.props.product.id)) {
            return true;
          } else {
            return false;
          }
        case AccessReportsAdminPageOptionsEnum.AllowMultiple:
          if (pageFormGroup.disallowedMultipleProducts.includes(this.props.product.id)) {
            return false;
          } else {
            return true;
          }
        default:
          console.error("Invalid Admin Page Option");
      }
    }

    return false;
  }

  private getPageOptionsPageElement = (
    byProductDefault: ByProductDefault,
    element: AccessReportsWizardElement,
    formGroup: AccessReportsWizardFormGroup,
    formGroupIndex: number,
    disabled: boolean,
  ): JSX.Element => {
    switch (element.elementType) {
      case AccessReportsWizardElementTypeEnum.Checkbox:
        return (
          <div className="d-flex align-items-center w-100 h-100">
            <QESCheckbox
              checked={byProductDefault[element.elementId] as boolean}
              disabled={disabled || this.props.disableUserActions}
              label={element.title}
              testId={`access-reports-admin__add-product--${element.elementId}-${formGroupIndex}`}
              onChange={() => this.props.onPageOptionPageChange(formGroup.groupPropertyId, byProductDefault.id, element.elementId, !byProductDefault[element.elementId])}
            />
          </div>
        );
      case AccessReportsWizardElementTypeEnum.Dropdown:
        return (
          <QESSelect
            allowEmpty={true}
            containerClassName="mb-3"
            disabled={disabled || this.props.disableUserActions}
            testId={`access-reports-admin__add-product--${element.elementId}-${formGroupIndex}`}
            hideLabel={!element.title}
            label={element.title}
            onChange={(e) => this.props.onPageOptionPageChange(formGroup.groupPropertyId, byProductDefault.id, element.elementId, e?.target?.value)}
            value={(byProductDefault[element.elementId])?.toString() || ""}
          >
            {this.getPageOptionPageDropdownOptions(element, formGroup)?.map((option, optionIndex) => (
              <option
                key={optionIndex}
                value={option.id}
              >
                {option.displayName ? option.displayName : option.name}
              </option>
            ))}
          </QESSelect>
        );
      case AccessReportsWizardElementTypeEnum.Input:
        const errorMessage: string = this.props.invalidPageOptionPageElements.get(formGroup.id + element.elementId);

        return (
          <QESTextInput
            containerClassName="mb-3"
            testId={`access-reports-admin__add-product--${element.elementId}-${formGroupIndex}`}
            disabled={disabled || this.props.disableUserActions}
            errorText={errorMessage}
            isValid={!errorMessage}
            unrequiredValidation={true}
            label={element.title}
            onChange={(e) => this.onPageOptionsPageDefaultInputChange(byProductDefault.id, formGroup.groupPropertyId, element.elementId, e)}
            onBlur={() => this.onPageOptionsPageDefaultInputChange(byProductDefault.id, formGroup.groupPropertyId, element.elementId, null, byProductDefault[element.elementId] as string)}
            value={byProductDefault[element.elementId] as string | number || ""}
          />
        );
      default:
        console.error(`Invalid Page Option Page Default Element Type: ${element.elementType}`);
        break;
    }
  }

  private getSidePanel = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum): JSX.Element => {
    let content: React.ReactNode | (() => React.ReactNode) = null;
    let title = "";

    switch (groupPropertyId) {
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages:
        return (
          <div className="float-right">
            <FlyoutLink
              classNames="qes-button-secondary p-0"
              content={{
                body: this.renderAccessSummaryEditChartColorsSidePanel(),
                title: "Edit Chart Colors"
              }}
              panelId={`access-reports-admin__add-product--${groupPropertyId}-side-panel-1`}
              size="40"
              testId={`access-reports-admin__add-product--${groupPropertyId}-side-panel-link-1`}
            >
              Edit Chart Colors
            </FlyoutLink>
            <span className="mx-3">
              |
            </span>
            <FlyoutLink
              classNames="qes-button-secondary p-0"
              content={{
                body: this.renderEditPrintColumnsSidePanel(groupPropertyId) as JSX.Element,
                title: "Edit Print Columns",
              }}
              panelId={`access-reports-admin__add-product--${groupPropertyId}-side-panel-2`}
              size="20"
              testId={`access-reports-admin__add-product--${groupPropertyId}-side-panel-link-2`}
            >
              Edit Print Columns
            </FlyoutLink>
          </div>
        );
      case AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages:
        content = this.renderAccessOverviewEditChartColorsSidePanel();
        title = "Edit Chart Colors";
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardDetailsPages:
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage:
      case AccessReportsWizardGroupPropertyIdEnum.AccessListingPages:
      case AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch:
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages:
        content = this.renderEditPrintColumnsSidePanel(groupPropertyId);
        title = "Edit Print Columns";
        break;
      default:
        break;
    }

    return (
      <FlyoutLink
        classNames="qes-button-secondary float-right p-0"
        content={{
          body: content as JSX.Element,
          title: title,
        }}
        panelId={`access-reports-admin__add-product--${groupPropertyId}-side-panel`}
        isVisible={this.state.sidePanelVisible}
        size="40"
        testId={`access-reports-admin__add-product--${groupPropertyId}-side-panel-link`}
      >
        {title}
      </FlyoutLink>
    );
  }

  private onAccessListingInputChange = (
    byProductDefaultId: number,
    groupPropertyId: AccessReportsWizardGroupPropertyIdEnum,
    elementId: string,
    event?: React.ChangeEvent<HTMLInputElement>,
    value?: string,
  ) => {
    if (!!event) {
      // on change
      const unparsedValue: string = event.target.value;
      let value: number = parseFloat(event.target.value);

      if (elementId.toUpperCase().includes("MILE")) {
        const re = /^([0-9]{0,4}|[0-9]{0,4}\.[0-9]?)$/;

        if (re.test(unparsedValue)) {
          if (value >= 1) {
            // remove leading zeros
            value = parseFloat(unparsedValue.replace(/^0+/, ""));
          }

          if ((!isNaN(value) && value >= 1 && value <= 9999.9) || !unparsedValue || unparsedValue === ".") {
            this.props.onPageOptionPageChange(groupPropertyId, byProductDefaultId, elementId, (!unparsedValue || unparsedValue === ".") ? unparsedValue : value);
          }
        }
      } else {
        let minValue = 0;
        let maxValue = 999;
        if (elementId.toUpperCase().includes("MINUTE")) {
          minValue = 1;
          maxValue = 9999;
        }

        if ((!isNaN(value) && value >= minValue && value <= maxValue) || !unparsedValue) {
          this.props.onPageOptionPageChange(groupPropertyId, byProductDefaultId, elementId, !unparsedValue ? unparsedValue : value);
        }
      }
    } else {
      if (elementId.toUpperCase().includes("COUNT")) {
        if (!value || value === "") {
          this.props.onPageOptionPageChange(groupPropertyId, byProductDefaultId, elementId, "0");
        }
      } else {
        if (!value || value === "" || value === "0" || value === "." || value === "0.") {
          this.props.onPageOptionPageChange(groupPropertyId, byProductDefaultId, elementId, "1");
        }
      }
    }
  }

  private onAccessRadiusSearchPageChange = (
    byProductDefaultId: number,
    groupPropertyId: AccessReportsWizardGroupPropertyIdEnum,
    elementId: string,
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const unparsedValue: string = event.target.value;
    let value: number = parseFloat(event.target.value);
    const re = /^([0-9]{0,4}|[0-9]{0,4}\.[0-9]?)$/;

    if (re.test(unparsedValue)) {
      if (value >= 1) {
        // remove leading zeros
        value = parseFloat(unparsedValue.replace(/^0+/, ""));
      }

      if ((!isNaN(value) && value >= 1 && value <= 999.9) || !unparsedValue) {
        this.props.onPageOptionPageChange(groupPropertyId, byProductDefaultId, elementId, (!unparsedValue || unparsedValue === ".") ? unparsedValue : value);
      }
    }
  }

  private onPageOptionsPageDefaultInputChange = (
    byProductDefaultId: number,
    groupPropertyId: AccessReportsWizardGroupPropertyIdEnum,
    elementId: string,
    event?: React.ChangeEvent<HTMLInputElement>,
    value?: string,
  ): void => {
    switch (groupPropertyId) {
      case AccessReportsWizardGroupPropertyIdEnum.AccessListingPages:
        this.onAccessListingInputChange(byProductDefaultId, groupPropertyId, elementId, event, value);
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch:
        if (!!event) {
          this.onAccessRadiusSearchPageChange(byProductDefaultId, groupPropertyId, elementId, event);
        }

        break;
      default:
        console.error(`Invalid Group Property Id: ${groupPropertyId}`)
        break;
    }
  }

  private registerTriggerClose = (triggerClose: () => void): void => {
    if (triggerClose) {
      if (!this.state.sidePanelVisible) {
        this.setState({
          sidePanelVisible: true,
        });
      }
    }
  }

  private renderAccessSummaryEditChartColorsSidePanel = (): JSX.Element => {
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(this.props.template, AccessReportsWizardStepEnum.PageOptions, AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages);
    const byProductDefault: ByProductDefault = pageFormGroup.byProductDefaults.find(x => x.productId === this.props.product.id);

    if (!!pageFormGroup) {
      return (
        <AccessSummaryEditChartColors
          byProductDefault={byProductDefault}
          disableButtons={this.props.disableUserActions}
          elements={pageFormGroup.elements.filter(x => Object.values(AccessSummaryEditChartColorsElementEnum).includes(x.elementId as AccessSummaryEditChartColorsElementEnum))}
          hideSidePanel={this.hideSidePanel}
          onSaveAccessSummaryChartColorsClick={this.onSaveAccessSummaryChartColorsClick}
        />
      );
    }

    return (
      <div className="form-group">
        <i className="text-danger fa fa-fw fa-exclamation-circle" />
        <span className="text-muted">
          Error
        </span>
      </div>
    );
  }

  private renderAccessOverviewEditChartColorsSidePanel = (): React.ReactNode => {
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(this.props.template, AccessReportsWizardStepEnum.PageOptions, AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages);
    const byProductDefault: ByProductDefault = pageFormGroup.byProductDefaults.find(x => x.productId === this.props.product.id);

    if (!!pageFormGroup) {
      return (
        <AccessOverviewEditChartColors
          byProductDefault={byProductDefault}
          disableButtons={this.props.disableUserActions}
          elements={pageFormGroup.elements}
          hideSidePanel={this.hideSidePanel}
          onSaveAccessOverviewChartColorsClick={this.onSaveAccessOverviewChartColorsClick}
        />
      );
    }

    return (
      <div className="form-group">
        <i className="text-danger fa fa-fw fa-exclamation-circle" />
        <span className="text-muted">
          Error
        </span>
      </div>
    );
  }

  private renderEditPrintColumnsSidePanel = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum): React.ReactNode => {
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(this.props.template, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);

    if (!!pageFormGroup) {
      const columnsElement: AccessReportsWizardElement = pageFormGroup.elements.find(x => x.elementId === "columns");
      const accessFilterElement: AccessReportsWizardElement = pageFormGroup.elements.find(x => x.elementId === "accessFilter");

      let accessFilters: AccessReportsWizardOption[] = [];
      if (groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage && groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages) {
        if (!!accessFilterElement?.options) {
          accessFilters = accessFilterElement.options;
        }
      }

      const columnDefaults: ColumnDefaults = pageFormGroup.columnDefaults.find(x => x.productId === this.props.product.id);

      if (!!columnsElement?.options) {
        return (
          <EditPrintColumns
            accessFilters={accessFilters}
            columnDefaults={columnDefaults}
            columns={columnsElement.options as AccessReportsColumn[]}
            disableButtons={this.props.disableUserActions}
            groupPropertyId={groupPropertyId}
            hideSidePanel={this.hideSidePanel}
            onSaveColumnsClick={this.onSaveColumnsClick}
          />
        );
      }
    }

    return (
      <div className="form-group">
        <i className="text-danger fa fa-fw fa-exclamation-circle" />
        <span className="text-muted">
          Error
        </span>
      </div>
    );
  }

  private splitKey = (key: string): string => {
    const splitKey: string = key.replace(/([A-Z])/g, " $1");
    const formattedKey: string = (splitKey.charAt(0).toUpperCase() + splitKey.slice(1));

    return formattedKey;
  }
}

export { AccessReportsAdminAddProductPageOptions };
